// The pure rules of the MPS board (spec 03 §4.4): the plan's weeks, each
// cell's state, the change against the approved plan and the grouping by
// planner code. Quantities stay strings of thousandths, so no float enters.

import type { BucketCellState } from "../../../../components/bucket-grid";
import { addDaysIso } from "../../../../components/bucket-grid-paste";
import type { MpsLine, MpsPlanProduct } from "../hooks/mps-queries";

/** The Mondays of a plan's horizon, oldest first. */
export function planWeeks(horizonStart: string, horizonWeeks: number): string[] {
  return Array.from({ length: Math.max(0, horizonWeeks) }, (_, i) =>
    addDaysIso(horizonStart, 7 * i),
  );
}

export const cellKey = (productId: string, week: string): string => `${productId}|${week}`;

/** Lines by product and week. */
export function indexLines(lines: readonly MpsLine[]): Map<string, MpsLine> {
  const map = new Map<string, MpsLine>();
  for (const line of lines) map.set(cellKey(line.product_id, line.bucket_start), line);
  return map;
}

/**
 * A cell's state as the grid draws it (spec 03 §4.4). A person's override wins,
 * then a dropped override, then the fence. A frozen cell that also lost an
 * override keeps its lock beside the red dot.
 */
export function lineCellState(line: MpsLine): { state: BucketCellState; locked: boolean } {
  if (line.override_qty !== null) return { state: "overridden", locked: line.is_frozen };
  if (line.override_dropped) return { state: "dropped", locked: line.is_frozen };
  if (line.is_frozen) return { state: "frozen", locked: false };
  return { state: "plain", locked: false };
}

const SIGNED = /^(-?)(\d+)(?:\.(\d{1,3}))?$/u;

/** A signed three-decimal string as thousandths. */
export function toThousandths(qty: string): bigint {
  const m = SIGNED.exec(qty);
  if (m === null) return 0n;
  const n = BigInt(m[2] ?? "0") * 1000n + BigInt((m[3] ?? "").padEnd(3, "0"));
  return m[1] === "-" ? -n : n;
}

/** Thousandths as a decimal string with three places, signed when negative. */
export function fromThousandths(n: bigint): string {
  const negative = n < 0n;
  const abs = negative ? -n : n;
  const whole = abs / 1000n;
  const frac = (abs % 1000n).toString().padStart(3, "0");
  return `${negative ? "-" : ""}${whole.toString()}.${frac}`;
}

/**
 * The change of each cell against the previous approved plan: this plan's
 * effective quantity less the previous one's, a cell missing from the
 * previous plan reading 0 (spec 03 §3.2's delta, cell by cell).
 */
export function cellChange(line: MpsLine, previous: MpsLine | undefined): string {
  const before = previous === undefined ? 0n : toThousandths(previous.effective_qty);
  return fromThousandths(toThousandths(line.effective_qty) - before);
}

/** Each product's net change against the previous plan over the weeks shown, largest first. */
export function productChanges(
  lines: readonly MpsLine[],
  previous: ReadonlyMap<string, MpsLine>,
): { productId: string; change: string; moved: bigint }[] {
  const sums = new Map<string, { change: bigint; moved: bigint }>();
  for (const line of lines) {
    const before = previous.get(cellKey(line.product_id, line.bucket_start));
    const d =
      toThousandths(line.effective_qty) -
      (before === undefined ? 0n : toThousandths(before.effective_qty));
    const at = sums.get(line.product_id) ?? { change: 0n, moved: 0n };
    sums.set(line.product_id, { change: at.change + d, moved: at.moved + (d < 0n ? -d : d) });
  }
  return [...sums.entries()]
    .filter(([, v]) => v.moved > 0n)
    .map(([productId, v]) => ({ productId, change: fromThousandths(v.change), moved: v.moved }))
    .sort((a, b) =>
      a.moved === b.moved ? a.productId.localeCompare(b.productId) : a.moved > b.moved ? -1 : 1,
    );
}

/** Products grouped by planner code in the order served, products with no code last. */
export function groupByPlanner(
  products: readonly MpsPlanProduct[],
): { plannerCode: string | null; products: MpsPlanProduct[] }[] {
  const groups = new Map<string, MpsPlanProduct[]>();
  for (const product of products) {
    const key = product.planner_code ?? "";
    const list = groups.get(key) ?? [];
    list.push(product);
    groups.set(key, list);
  }
  return [...groups.entries()]
    .sort(([a], [b]) => (a === "" ? 1 : b === "" ? -1 : a.localeCompare(b)))
    .map(([code, list]) => ({ plannerCode: code === "" ? null : code, products: list }));
}

/** The base unit whose delta decided the route, the largest of `delta_by_uom`. */
export function decidingUnit(
  byUom: readonly { uom_id: string; delta_pct: string }[] | null,
): { uomId: string; pct: string } | undefined {
  let best: { uomId: string; pct: string } | undefined;
  for (const entry of byUom ?? []) {
    if (best === undefined || Number(entry.delta_pct) > Number(best.pct)) {
      best = { uomId: entry.uom_id, pct: entry.delta_pct };
    }
  }
  return best;
}

/** True when a stored delta is above a bar; both are two-decimal strings. */
export function aboveBar(deltaPct: string, barPct: string): boolean {
  return Math.round(Number(deltaPct) * 100) > Math.round(Number(barPct) * 100);
}
